import React, { Component } from 'react';
import Select, { MultiValue } from 'react-select';
import { fetchApi } from '@api/fetchApi';
import { InputFieldWrapper } from '@components-shared/forms/FieldLabel';
import { getCustomStyles } from '@components-shared/forms/MultiSelect/getCustomStyles';
import {
  IMultiSelectProps,
  IMultiSelectState,
  IMultiSelectOptionInternal,
  IFetchOption,
  IMultiSelectOption,
} from '@components-shared/forms/MultiSelect/interfaces';
import { keyMap } from '@locales/lokalise/keyMap';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import cn from 'classnames';
import { connectToContext } from 'contextStore';
import { ApiType } from '../formField.interface';

class MultiSelect extends Component<IMultiSelectProps, IMultiSelectState> {
  state: IMultiSelectState = {
    disabled: true,
    options: [],
  };

  async componentDidMount(): Promise<void> {
    const { api, attributes = { disabled: false } } = this.props;

    const { disabled } = attributes;

    if (api) {
      await this.fetchOptions(api, disabled);
    } else {
      this.setState({ disabled: attributes.disabled });
    }
  }

  fetchOptions = async (api: ApiType, disabled: boolean): Promise<void> => {
    try {
      const response = await fetchApi.post<IFetchOption[] | string[]>(api);
      const options = response.data.map((option: IFetchOption | string) => {
        return typeof option === 'object'
          ? {
              value: option.id || option.value,
              label: option.name,
            }
          : {
              value: option,
              label: option,
            };
      });

      this.setState({ options, disabled });
    } catch (error) {
      handleError('Failed to fetch options', error);
    }
  };

  changeLastOptionToFirst = (options: MultiValue<IMultiSelectOptionInternal>): (string | number)[] => {
    const optionsList = options.map((v) => v.value);

    if (optionsList.length >= 2) {
      const lastOptionIndex = optionsList.length - 1;
      const modifiedOptions = optionsList.slice(0, lastOptionIndex);

      modifiedOptions.unshift(optionsList[lastOptionIndex]);

      return modifiedOptions;
    }
    return optionsList;
  };

  onChange = (value: MultiValue<IMultiSelectOptionInternal>): void => {
    const { isLabelMatchValue = true } = this.props;

    const convertedValues = value
      ? isLabelMatchValue
        ? this.changeLastOptionToFirst(value)
        : value.map((v) => v.value)
      : [];

    this.props.onChange(this.props.name, convertedValues as string[] | IMultiSelectOption[]);
  };

  render(): JSX.Element {
    const {
      name,
      title,
      options,
      valid,
      validationMessage,
      value,
      readOnly,
      required,
      isLabelMatchValue = true,
      tooltip,
      i18next,
      maxCount,
    } = this.props;

    const { disabled, options: stateOptions } = this.state;

    const isSelectDisabled = Boolean(readOnly || disabled);

    const customStyles = getCustomStyles({ valid });

    const optionList = options.map((opt) => {
      const optionTitle = opt.name || opt.value.toString();

      return { value: opt.value, label: i18next.t(optionTitle as KeyMapKeys) };
    });

    const optionsCombined = [...stateOptions, ...optionList];

    const valuesList = (value as string[]).map((currentValue) => {
      return isLabelMatchValue
        ? { value: currentValue, label: i18next.t(currentValue as KeyMapKeys) }
        : {
            value: currentValue,
            label: optionsCombined.find((option) => option.value === currentValue)?.label,
          };
    });

    return (
      <InputFieldWrapper
        title={title}
        tooltip={tooltip}
        required={required}
        valid={valid}
        validationMessage={validationMessage}>
        <Select
          isOptionDisabled={() => !!maxCount && valuesList.length >= maxCount}
          placeholder={`-- ${i18next.t(keyMap.formsSelect)} --`}
          isMulti
          closeMenuOnSelect={false}
          className={cn('form__multiselect', { invalid: !valid })}
          classNamePrefix="react-select"
          name={name}
          value={valuesList}
          onChange={this.onChange}
          options={optionsCombined}
          isDisabled={isSelectDisabled}
          styles={customStyles}
        />
      </InputFieldWrapper>
    );
  }
}
const MultiSelectWithContext = connectToContext(MultiSelect);

export { MultiSelectWithContext as MultiSelect };
